import React, { useMemo } from 'react';
import sanitizer from '@helpers/sanitizer';
import useBreadcrumbsQuery from '@hooks/product/use-breadcrumbs-query';
import { useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import { usePreferences } from '@context/preferences.context';
import useFormatPrice from '@hooks/use-format-price-hook';
import useInitialRequestUrlQuery from '@hooks/use-initial-request-url-query';
import { ElasticSearchProduct } from '@interfaces/api/responses/elasticSearch';
import { getSeoBaseUrlFromLocale } from '@helpers/utils/seo-structured-data';
import { createCatalogProductSchema } from './product-schema-utils';

const MAX_NUMBER_OF_ITEMS = 10000;

const CatalogSchema: React.FC = () => {
  const { breadcrumbs } = useBreadcrumbsQuery();
  const { convertFromCents } = useFormatPrice();
  const products = useContextSelector(CatalogContext, (v) => v.products);
  const queryResult = useContextSelector(CatalogContext, (v) => v.queryResult);
  const totalResults = useContextSelector(CatalogContext, (v) => v.totalResults);
  const priceBound = useContextSelector(CatalogContext, (v) => v.priceBound);
  const { initialRequestUrl } = useInitialRequestUrlQuery();
  const { currency, locale } = usePreferences();
  const queryResultFacets = useMemo(() => queryResult?.facets?.fields ?? {}, [queryResult]);

  const productSchema = useMemo(() => {
    const schemaName = breadcrumbs?.[breadcrumbs.length - 1]?.localizedName;
    const schemaBrand = queryResultFacets?.brand?.length === 1 ? queryResultFacets.brand[0].name : '';
    const lowPriceCents = priceBound?.lower;
    const highPriceCents = priceBound?.upper;
    const aggregateOffer =
      typeof lowPriceCents === 'number' && typeof highPriceCents === 'number' && highPriceCents > 0
        ? {
            offers: {
              '@type': 'AggregateOffer',
              lowPrice: convertFromCents(lowPriceCents),
              highPrice: convertFromCents(highPriceCents),
              priceCurrency: currency,
            },
          }
        : {};

    return {
      '@context': 'http://schema.org',
      '@type': 'Product',
      name: schemaName,
      ...(schemaBrand && {
        brand: {
          '@type': 'Brand',
          name: schemaBrand,
        },
      }),
      ...aggregateOffer,
    };
  }, [breadcrumbs, queryResultFacets, priceBound, convertFromCents, currency]);

  const itemListSchema = useMemo(() => {
    if (!products?.length || !queryResult?.items) {
      return null;
    }

    const origin = getSeoBaseUrlFromLocale(locale);

    const searchItemsById = new Map<string, ElasticSearchProduct>();
    queryResult.items.forEach((item: ElasticSearchProduct) => {
      searchItemsById.set(String(item.id), item);
    });

    const itemListElement = products.map((product, index: number) => {
      const searchItem = searchItemsById.get(product.id);

      return {
        '@type': 'ListItem',
        position: index + 1,
        item: createCatalogProductSchema(product, searchItem, origin),
      };
    });

    const pageUrl =
      origin && initialRequestUrl ? `${origin}${initialRequestUrl.pathname}${initialRequestUrl.search}` : undefined;

    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      ...(totalResults != null && { numberOfItems: Math.min(totalResults, MAX_NUMBER_OF_ITEMS) }),
      ...(pageUrl && { url: pageUrl }),
      itemListElement,
    };
  }, [products, queryResult, totalResults, initialRequestUrl, locale]);

  if (!itemListSchema) {
    return null;
  }

  return (
    <>
      <script
        data-cy="catalog-product-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: sanitizer(JSON.stringify(productSchema)) }}
      />
      <script
        data-cy="catalog-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: sanitizer(JSON.stringify(itemListSchema)) }}
      />
    </>
  );
};

export default CatalogSchema;
